import { useState } from "react"
import { Check, Plus, Tags as TagsIcon } from "lucide-react"
import { api } from "../../api/client"
import { useApp } from "../../state/AppState"
import { accent, accentFor } from "../../design/palette"
import { Button, Input, Popover, TagChip, cx } from "../ui"

/**
 * Attach tags to an issue, and create one inline when the right label does not
 * exist yet - the alternative is making people leave the thread mid-thought to
 * visit the tag manager. Only the project owner may create one, so for anyone
 * else the inline form is left out rather than shown and refused.
 */
export function TagSelector({
	projectId,
	tags,
	selected,
	onChange,
	onTagCreated,
	canCreate = true
}) {
	const { toast } = useApp()
	const [draft, setDraft] = useState("")
	const [creating, setCreating] = useState(false)

	const chosen = tags.filter((tag) => selected.includes(tag.tag_id))

	function toggle(tagId) {
		onChange(
			selected.includes(tagId)
				? selected.filter((id) => id !== tagId)
				: [...selected, tagId]
		)
	}

	async function createTag() {
		const name = draft.trim()
		if (!name) return
		setCreating(true)
		try {
			const tag = await api.createTag(projectId, {
				name,
				colour: accentFor(name)
			})
			setDraft("")
			onTagCreated?.(tag)
			onChange([...selected, tag.tag_id])
			toast(`Created tag "${tag.name}"`, "success")
		} catch (cause) {
			toast(cause.message, "error")
		} finally {
			setCreating(false)
		}
	}

	return (
		<div className="flex flex-wrap items-center gap-1.5">
			{chosen.map((tag) => (
				<TagChip key={tag.tag_id} tag={tag} onRemove={() => toggle(tag.tag_id)} />
			))}

			<Popover
				align="left"
				className="w-64"
				trigger={({ toggle: openMenu, open }) => (
					<button
						type="button"
						onClick={openMenu}
						aria-expanded={open}
						className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-ink-300 px-2.5 py-1 text-xs font-medium text-ink-500 transition-colors hover:border-ink-400 hover:text-ink-700"
					>
						<TagsIcon size={12} aria-hidden />
						{chosen.length ? "Edit tags" : "Add tags"}
					</button>
				)}
			>
				{() => (
					<>
						<div className="max-h-56 overflow-y-auto scrollbar-slim">
							{tags.length === 0 && (
								<p className="px-2.5 py-2 text-2xs leading-relaxed text-ink-500">
									{canCreate
										? "No tags in this project yet. Create the first one below."
										: "No tags in this project yet. The project owner can add them."}
								</p>
							)}
							{tags.map((tag) => {
								const on = selected.includes(tag.tag_id)
								const tone = accent(tag.colour)
								return (
									<button
										key={tag.tag_id}
										type="button"
										onClick={() => toggle(tag.tag_id)}
										className={cx(
											"flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[0.8125rem] transition-colors",
											on ? "bg-brand-50 text-ink-900" : "text-ink-700 hover:bg-ink-100"
										)}
									>
										<span
											className={cx("h-2 w-2 shrink-0 rounded-full", tone.dot)}
											aria-hidden
										/>
										<span className="min-w-0 flex-1 truncate">{tag.name}</span>
										{on && <Check size={13} className="shrink-0 text-brand-700" aria-hidden />}
									</button>
								)
							})}
						</div>

						{canCreate && (
							<div className="mt-1 flex gap-1.5 border-t border-ink-100 px-1.5 pb-1 pt-2">
								<Input
									value={draft}
									onChange={(event) => setDraft(event.target.value)}
									onKeyDown={(event) => {
										if (event.key === "Enter") {
											event.preventDefault()
											createTag()
										}
									}}
									placeholder="New tag name"
									className="h-8 text-[0.8125rem]"
									maxLength={40}
								/>
								<Button
									type="button"
									size="sm"
									variant="subtle"
									icon={Plus}
									onClick={createTag}
									busy={creating}
									disabled={creating || !draft.trim()}
								>
									Add
								</Button>
							</div>
						)}
					</>
				)}
			</Popover>
		</div>
	)
}
